import { Component } from '@theme/component';

/**
 * `<tasting-notes-pdf-component>` turns the product's tasting notes into a
 * downloadable PDF when the product has no uploaded tasting-notes file.
 *
 * The PDF is generated client-side with pdf-lib (vendored, pinned 1.17.1;
 * loaded on demand so it never affects page load), laid out like the original
 * tasting-note PDFs: logo top right, title, then the notes flowing across two
 * columns. Text uses the standard PDF font (Helvetica): embedding the brand
 * font required fontkit, whose subsetter crashes on some glyph combinations
 * and whose embedded fonts render with broken spacing in Safari/Preview.
 * The content source is the server-rendered `<template>` in the block.
 *
 * If generation fails (script/font fetch, canvas), it falls back to printing
 * the same template via a hidden iframe (browser print dialog, Save as PDF).
 *
 * @extends {Component}
 */

/** Page geometry in PDF points (A4). */
const PAGE = {
  width: 595.28,
  height: 841.89,
  marginX: 54, // 19mm
  marginTop: 45, // 16mm
  marginBottom: 57, // 20mm
};
const LOGO_WIDTH = 76.5; // 27mm
const TITLE_SIZE = 13;
const BODY_SIZE = 10;
// Spacing tuned so every migrated note fits a single page (like the originals)
const LINE_HEIGHT = 14.5; // 10pt * 1.45
const PARAGRAPH_GAP = 11.5; // ≈4mm
const HEADING_EXTRA = 9; // extra space above a section heading
const TITLE_GAP = 24; // logo bottom -> title baseline
const NOTES_GAP = 20; // title baseline -> top of the columns
const COLUMN_GAP = 25.5; // 9mm
const COLUMN_WIDTH = (PAGE.width - 2 * PAGE.marginX - COLUMN_GAP) / 2;

class TastingNotesPdfComponent extends Component {
  requiredRefs = ['template', 'button'];

  /** @type {HTMLIFrameElement | null} */
  #iframe = null;

  #busy = false;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#iframe?.remove();
    this.#iframe = null;
  }

  /**
   * Generates and downloads the tasting-note PDF.
   */
  async print() {
    if (this.#busy) return;
    this.#busy = true;
    this.refs.button.setAttribute('aria-busy', 'true');

    try {
      await this.#downloadPdf();
    } catch (error) {
      console.warn('[tasting-notes-pdf] PDF generation failed, falling back to the print dialog', error);
      await this.#printFallback();
    } finally {
      this.refs.button.removeAttribute('aria-busy');
      this.#busy = false;
    }
  }

  async #downloadPdf() {
    const { PDFDocument, StandardFonts, rgb } = await import(this.dataset.pdfLibSrc);

    const pdf = await PDFDocument.create();

    const title = this.dataset.documentTitle ?? document.title;
    pdf.setTitle(title);

    const regular = await pdf.embedFont(StandardFonts.Helvetica);
    const medium = await pdf.embedFont(StandardFonts.HelveticaBold);
    const black = rgb(0, 0, 0);

    let page = pdf.addPage([PAGE.width, PAGE.height]);
    const pageTop = PAGE.height - PAGE.marginTop;

    // Logo, top right
    const logo = await this.#logoPng();
    let logoBottom = pageTop;
    if (logo) {
      const image = await pdf.embedPng(logo);
      const logoHeight = LOGO_WIDTH * (image.height / image.width);
      logoBottom = pageTop - logoHeight;
      page.drawImage(image, {
        x: PAGE.width - PAGE.marginX - LOGO_WIDTH,
        y: logoBottom,
        width: LOGO_WIDTH,
        height: logoHeight,
      });
    }

    // Title
    const titleBaseline = logoBottom - TITLE_GAP;
    page.drawText(this.#printTitle(), {
      x: PAGE.marginX,
      y: titleBaseline,
      size: TITLE_SIZE,
      font: regular,
      color: black,
    });

    // Notes in two columns; continuation pages use the full height
    let columnTop = titleBaseline - NOTES_GAP;
    let column = 0;
    let y = columnTop;

    const nextColumn = () => {
      if (column === 0) {
        column = 1;
      } else {
        page = pdf.addPage([PAGE.width, PAGE.height]);
        column = 0;
        columnTop = pageTop;
      }
      y = columnTop;
    };

    const drawLine = (text, font) => {
      if (y - LINE_HEIGHT < PAGE.marginBottom) nextColumn();
      y -= LINE_HEIGHT;
      page.drawText(text, {
        x: PAGE.marginX + column * (COLUMN_WIDTH + COLUMN_GAP),
        y,
        size: BODY_SIZE,
        font,
        color: black,
      });
    };

    for (const block of this.#blocks()) {
      const font = block.bold ? medium : regular;
      if (block.heading) {
        // keep the heading attached to at least two lines of its paragraph
        if (y !== columnTop && y - (HEADING_EXTRA + 3 * LINE_HEIGHT) < PAGE.marginBottom) nextColumn();
        if (y !== columnTop) y -= HEADING_EXTRA;
      }
      for (const line of this.#wrap(block.text, font, BODY_SIZE, COLUMN_WIDTH)) {
        drawLine(line, font);
      }
      y -= PARAGRAPH_GAP;
    }

    const bytes = await pdf.save();
    const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title.replace(/[\\/:*?"<>|]+/g, ' ').trim()}.pdf`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  }

  /**
   * The notes as flat blocks: `{ heading, bold, text }`. The opening
   * paragraph is bold, as in the original tasting-note PDFs.
   *
   * @returns {{ heading: boolean, bold: boolean, text: string }[]}
   */
  #blocks() {
    const notes = this.refs.template.content.querySelector('.pdf-note__notes');
    const blocks = [];
    let firstParagraph = true;
    for (const el of notes?.querySelectorAll('p, h1, h2, h3, h4, h5, h6') ?? []) {
      const text = el.textContent?.replace(/\s+/g, ' ').trim();
      if (!text) continue;
      const heading = el.tagName !== 'P';
      blocks.push({ heading, bold: !heading && firstParagraph, text });
      if (!heading) firstParagraph = false;
    }
    return blocks;
  }

  /**
   * Rasterizes the inline logo SVG (which uses `currentColor` → black) at
   * print resolution.
   *
   * @returns {Promise<ArrayBuffer | null>}
   */
  async #logoPng() {
    const svg = this.refs.template.content.querySelector('svg');
    if (!svg) return null;

    const clone = /** @type {SVGElement} */ (svg.cloneNode(true));
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    const [, , viewWidth, viewHeight] = (clone.getAttribute('viewBox') ?? '0 0 285 102').split(/\s+/).map(Number);
    const width = 1200; // ≈400dpi at the printed logo size
    const height = Math.round(width * (viewHeight / viewWidth));
    clone.setAttribute('width', String(width));
    clone.setAttribute('height', String(height));

    const url = URL.createObjectURL(new Blob([clone.outerHTML], { type: 'image/svg+xml' }));
    try {
      const image = new Image();
      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = () => reject(new Error('logo failed to load'));
        image.src = url;
      });
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d')?.drawImage(image, 0, 0, width, height);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
      return blob ? await blob.arrayBuffer() : null;
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  /**
   * Greedy word-wrap using the embedded font's metrics.
   *
   * @param {string} text
   * @param {import('pdf-lib').PDFFont} font
   * @param {number} size
   * @param {number} width
   * @returns {string[]}
   */
  #wrap(text, font, size, width) {
    const lines = [];
    let line = '';
    for (const word of text.split(' ')) {
      const candidate = line ? `${line} ${word}` : word;
      if (!line || font.widthOfTextAtSize(candidate, size) <= width) {
        line = candidate;
      } else {
        lines.push(line);
        line = word;
      }
    }
    if (line) lines.push(line);
    return lines;
  }

  #printTitle() {
    const title = this.refs.template.content.querySelector('.pdf-note__title');
    return title?.textContent?.replace(/\s+/g, ' ').trim() ?? this.dataset.documentTitle ?? '';
  }

  /**
   * Fallback: prints the server-rendered template from a hidden iframe; the
   * user saves the PDF from the browser's print dialog.
   */
  async #printFallback() {
    const iframe = this.#iframe ?? this.#createPrintFrame();
    if (!iframe?.contentDocument || !iframe.contentWindow) return;

    const { fonts } = iframe.contentDocument;
    await Promise.race([fonts.ready, new Promise((resolve) => setTimeout(resolve, 2000))]);
    await new Promise(requestAnimationFrame);

    iframe.contentWindow.focus();
    iframe.contentWindow.print();
  }

  /**
   * @returns {HTMLIFrameElement | null}
   */
  #createPrintFrame() {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.setAttribute('tabindex', '-1');
    // Off-screen but laid out — `display: none` frames refuse to print.
    iframe.style.cssText =
      'position: fixed; inset-block-start: 0; inset-inline-start: -200vw; width: 210mm; height: 297mm; visibility: hidden;';
    document.body.append(iframe);

    const doc = iframe.contentDocument;
    if (!doc) {
      iframe.remove();
      return null;
    }

    doc.title = this.dataset.documentTitle ?? document.title;
    doc.body.append(this.refs.template.content.cloneNode(true));

    this.#iframe = iframe;
    return iframe;
  }
}

if (!customElements.get('tasting-notes-pdf-component')) {
  customElements.define('tasting-notes-pdf-component', TastingNotesPdfComponent);
}
